// src/components/sales/pages/clients/ClientsView.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Plus,
  RefreshCw,
  Users,
  Pencil,
  Trash2,
  X,
  Search,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import ClientFormModal from "./ClientFormModal";

export default function ClientsView({
  clients,
  dataBlocks,
  loading,
  onRefresh,
  onCreate,
  onUpdate,
  onDelete,

  message,
  onClearMessage,
}) {
  const { t, i18n } = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const textAlign = i18n.dir() === "rtl" ? "text-right" : "text-right";

  // ✅ Debounced Search
  const [searchInput, setSearchInput] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [searchScope, setSearchScope] = useState("all"); // all | company | contact | email | phone | dataBlock | status

  // ✅ Pagination (client-side)
  const PAGE_SIZE = 5;
  const [page, setPage] = useState(1); // 1-based

  useEffect(() => {
    const tt = setTimeout(() => setSearchApplied(searchInput), 350);
    return () => clearTimeout(tt);
  }, [searchInput]);

  const headers = useMemo(
    () => [
      t("sales.clients.table.company"),
      t("sales.clients.table.contact"),
      t("sales.clients.table.emailPhone"),
      t("sales.clients.table.status"),
      t("sales.clients.table.dataBlock"),
      t("sales.clients.table.actions"),
    ],
    [t]
  );

  const openCreate = () => {
    setEditing(null);
    setOpenModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
    setEditing(null);
  };

  const safeId = (row) => {
    const v = row?.id ?? row?.clientId ?? row?.clientID ?? row?.client_id;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const filteredClients = useMemo(() => {
    const list = Array.isArray(clients) ? clients : [];
    const term = (searchApplied || "").trim().toLowerCase();
    if (!term) return list;

    const fullNameOf = (c) =>
      `${c?.designation ? `${c.designation} ` : ""}${c?.firstName || ""} ${c?.secondName || ""}`.trim();

    const idStrOf = (c) => String(c?.id ?? c?.clientId ?? c?.clientID ?? c?.client_id ?? "");

    const dataBlockOf = (c) => c?.dataBlock ?? c?.dataBlockName ?? c?.blockName ?? "";
    const statusOf = (c) => c?.clientStatus ?? c?.status ?? c?.client_state ?? "";

    const haystack = (c) => {
      const fullName = fullNameOf(c);
      const idStr = idStrOf(c);

      switch (searchScope) {
        case "company":
          return [c?.companyName, c?.industry, c?.companyType, idStr]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

        case "contact":
          return [fullName, c?.position, idStr].filter(Boolean).join(" ").toLowerCase();

        case "email":
          return [c?.emailAddress, idStr].filter(Boolean).join(" ").toLowerCase();

        case "phone":
          return [c?.phoneNumber, idStr].filter(Boolean).join(" ").toLowerCase();

        case "dataBlock":
          return [dataBlockOf(c), idStr].filter(Boolean).join(" ").toLowerCase();

        case "status":
          return [statusOf(c), idStr].filter(Boolean).join(" ").toLowerCase();

        case "all":
        default:
          return [
            c?.companyName,
            c?.industry,
            c?.companyType,
            fullName,
            c?.position,
            c?.emailAddress,
            c?.phoneNumber,
            statusOf(c),
            dataBlockOf(c),
            idStr,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
      }
    };

    return list.filter((c) => haystack(c).includes(term));
  }, [clients, searchApplied, searchScope]);

  // ✅ Reset page on search/scope/data change
  useEffect(() => {
    setPage(1);
  }, [searchApplied, searchScope, clients]);

  const totalCount = clients?.length || 0;
  const shownCount = filteredClients?.length || 0;

  const searching = (searchApplied || "").trim().length > 0;
  const typing = (searchInput || "").trim().length > 0;

  const clearSearch = () => {
    setSearchInput("");
    setSearchApplied("");
  };

  const scopeLabel =
    searchScope === "all"
      ? t("sales.clients.search.scope.all")
      : searchScope === "company"
      ? t("sales.clients.search.scope.company")
      : searchScope === "contact"
      ? t("sales.clients.search.scope.contact")
      : searchScope === "email"
      ? t("sales.clients.search.scope.email")
      : searchScope === "phone"
      ? t("sales.clients.search.scope.phone")
      : searchScope === "dataBlock"
      ? t("sales.clients.search.scope.dataBlock")
      : t("sales.clients.search.scope.status");

  const placeholder =
    searchScope === "company"
      ? t("sales.clients.search.placeholder.company")
      : searchScope === "contact"
      ? t("sales.clients.search.placeholder.contact")
      : searchScope === "email"
      ? t("sales.clients.search.placeholder.email")
      : searchScope === "phone"
      ? t("sales.clients.search.placeholder.phone")
      : searchScope === "dataBlock"
      ? t("sales.clients.search.placeholder.dataBlock")
      : searchScope === "status"
      ? t("sales.clients.search.placeholder.status")
      : t("sales.clients.search.placeholder.all");

  // ✅ Pagination derived
  const totalPages = Math.max(1, Math.ceil(shownCount / PAGE_SIZE));
  const safePageNumber = Math.min(Math.max(1, page), totalPages);

  const pagedClients = useMemo(() => {
    const arr = Array.isArray(filteredClients) ? filteredClients : [];
    const start = (safePageNumber - 1) * PAGE_SIZE;
    return arr.slice(start, start + PAGE_SIZE);
  }, [filteredClients, safePageNumber]);

  const goToPage = (p) => {
    const next = Math.min(Math.max(1, p), totalPages);
    setPage(next);
  };

  const startIndex = shownCount === 0 ? 0 : (safePageNumber - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(shownCount, safePageNumber * PAGE_SIZE);

  const pageButtons = useMemo(() => {
    const windowSize = 5;
    let start = Math.max(1, safePageNumber - Math.floor(windowSize / 2));
    let end = start + windowSize - 1;
    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - windowSize + 1);
    }
    const arr = [];
    for (let i = start; i <= end; i++) arr.push(i);
    return arr;
  }, [safePageNumber, totalPages]);

  return (
    <div className="text-white">
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-xl bg-white/10 border border-white/20 backdrop-blur-xl">
            <Users className="w-6 h-6 text-pink-200" />
          </div>
          <div>
            <h2 className="text-xl font-bold">{t("sales.clients.title")}</h2>
            <p className="text-xs text-white/70">{t("sales.clients.subtitle")}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={openCreate}
            className="px-4 py-2 rounded-xl bg-purple-600/80 hover:bg-purple-700 text-white flex items-center gap-2 backdrop-blur-lg disabled:opacity-50"
            disabled={loading}
          >
            <Plus className="w-5 h-5" />
            {t("sales.clients.actions.add")}
          </button>

          <button
            onClick={onRefresh}
            className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white flex items-center gap-2 backdrop-blur-lg disabled:opacity-50"
            disabled={loading}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
            {t("common.refresh")}
          </button>
        </div>
      </div>

      {/* Message Banner */}
      {message?.text ? (
        <div
          className={[
            "mb-6 rounded-2xl px-4 py-3 text-sm border backdrop-blur-xl flex items-start justify-between gap-3",
            message.type === "error"
              ? "border-red-500/30 bg-red-500/10 text-red-200"
              : "border-emerald-500/30 bg-emerald-500/10 text-emerald-200",
          ].join(" ")}
        >
          <div className="whitespace-pre-wrap">{message.text}</div>
          {onClearMessage && (
            <button
              type="button"
              onClick={onClearMessage}
              className="shrink-0 p-2 rounded-lg bg-white/10 hover:bg-white/15 border border-white/10"
              title={t("sales.clients.actions.dismiss")}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      ) : null}

      {/* Table Card */}
      <div className="backdrop-blur-xl bg-white/10 rounded-2xl shadow-[0_0_30px_rgba(255,255,255,0.06)] border border-white/20 overflow-hidden">
        {/* Top bar */}
        <div className="p-6 bg-gradient-to-r from-white/5 via-white/10 to-white/5 backdrop-blur-xl">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <h3 className="text-lg font-bold">{t("sales.clients.listTitle")}</h3>

            <div className="text-sm text-gray-200">
              {searching ? (
                <span>{t("sales.clients.showingCount", { shown: shownCount, total: totalCount })}</span>
              ) : (
                <span>{t("sales.clients.totalCount", { total: totalCount })}</span>
              )}
            </div>
          </div>

          {/* ✅ Search (Debounced + chips - calm theme) */}
          <div className="mt-4">
            {/* Chips */}
            <div className="flex flex-wrap gap-2 justify-end">
              {[
                { key: "all", label: t("sales.clients.search.scope.allShort") },
                { key: "company", label: t("sales.clients.search.scope.company") },
                { key: "contact", label: t("sales.clients.search.scope.contact") },
                { key: "email", label: t("sales.clients.search.scope.email") },
                { key: "phone", label: t("sales.clients.search.scope.phone") },
                { key: "dataBlock", label: t("sales.clients.search.scope.dataBlock") },
                { key: "status", label: t("sales.clients.search.scope.status") },
              ].map((chip) => {
                const active = searchScope === chip.key;
                return (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => setSearchScope(chip.key)}
                    className={[
                      "px-3 py-1.5 rounded-xl text-xs border backdrop-blur-xl transition",
                      active
                        ? "bg-white/15 border-white/25 text-white"
                        : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10",
                    ].join(" ")}
                  >
                    {chip.label}
                  </button>
                );
              })}
            </div>

            {/* Input */}
            <div className="mt-3 w-full">
              <div className="rounded-2xl border border-white/15 bg-white/5 backdrop-blur-xl">
                <div className="relative rounded-2xl">
                  <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/55" />

                  <input
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder={placeholder}
                    className="w-full rounded-2xl bg-transparent px-4 py-3 pr-11 text-right text-sm text-white placeholder:text-white/45 outline-none
                               focus:border-white/25 focus:ring-2 focus:ring-white/10"
                  />

                  {(typing || searching) && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="absolute left-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center
                                 w-9 h-9 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition"
                      title={t("sales.clients.actions.clear")}
                    >
                      <X className="w-4 h-4 text-white/70" />
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-2 text-xs text-white/55 text-right flex items-center justify-end gap-2">
                <span>
                  {t("sales.clients.searchingIn")}{" "}
                  <span className="text-white/75 font-medium">{scopeLabel}</span>
                </span>
                {typing && searchInput !== searchApplied && (
                  <span className="px-2 py-0.5 rounded-lg bg-white/5 border border-white/10 text-white/60">
                    {t("sales.clients.applying")}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-white">
            <thead className="bg-white/10">
              <tr>
                {headers.map((h) => (
                  <th key={h} className={`px-4 py-3 ${textAlign} text-sm font-semibold`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-white/10">
              {(pagedClients || []).map((c) => {
                const id = safeId(c);

                const fullName = `${c.designation ? `${c.designation} ` : ""}${c.firstName || ""} ${
                  c.secondName || ""
                }`.trim();

                return (
                  <tr
                    key={id ?? `${c.emailAddress || "row"}-${c.phoneNumber || ""}`}
                    className="hover:bg-white/10 transition"
                  >
                    <td className="px-4 py-3 text-right">
                      <div className="font-semibold">{c.companyName || "-"}</div>
                      <div className="text-xs text-gray-300">
                        {c.industry || ""}
                        {c.industry && c.companyType ? " • " : ""}
                        {c.companyType || ""}
                      </div>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <div className="text-sm">{fullName || "-"}</div>
                      <div className="text-xs text-gray-300">{c.position || "-"}</div>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <div className="text-sm">{c.emailAddress || "-"}</div>
                      <div className="text-xs text-gray-300">{c.phoneNumber || "-"}</div>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <span className="px-2 py-1 rounded text-xs bg-white/10 border border-white/20">
                        {c.clientStatus || c.status || "-"}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <span className="px-2 py-1 rounded text-xs bg-purple-500/15 text-purple-200 border border-white/15">
                        {c.dataBlock || c.dataBlockName || "-"}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => openEdit(c)}
                          className="px-3 py-1.5 rounded-lg bg-pink-600/80 hover:bg-pink-700 text-white text-xs backdrop-blur-lg flex items-center gap-1 disabled:opacity-50"
                          disabled={loading}
                        >
                          <Pencil className="w-4 h-4" />
                          {t("common.edit")}
                        </button>

                        <button
                          onClick={() => {
                            if (id == null) return;
                            onDelete?.(id);
                          }}
                          className="px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 border border-red-400/30 text-red-100 text-xs backdrop-blur-lg flex items-center gap-1 disabled:opacity-50"
                          disabled={loading || id == null}
                          title={id == null ? t("sales.clients.invalidClientId") : t("common.delete")}
                        >
                          <Trash2 className="w-4 h-4" />
                          {t("common.delete")}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {!loading && shownCount === 0 && (
                <tr>
                  <td className="px-4 py-10 text-center text-gray-200" colSpan={6}>
                    {searching ? t("sales.clients.emptySearch") : t("sales.clients.empty")}
                  </td>
                </tr>
              )}

              {loading && (
                <tr>
                  <td className="px-4 py-10 text-center text-gray-200" colSpan={6}>
                    {t("common.loading")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ✅ Pagination Footer */}
        {/* ✅ Pagination Footer */}
<div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between px-4 py-4 border-t border-white/10 bg-white/5">
  <div className="text-xs text-white/70 text-right">
    {shownCount === 0 ? (
      "—"
    ) : (
      <span>
        {t("sales.clients.pagination.showingRange", {
          start: startIndex,
          end: endIndex,
          total: shownCount,
        })}
      </span>
    )}
  </div>

  <div className="flex items-center gap-2 justify-end">
    <button
      type="button"
      onClick={() => goToPage(safePageNumber - 1)}
      disabled={loading || safePageNumber <= 1}
      className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white disabled:opacity-50 flex items-center gap-2"
    >
      <ChevronRight className="w-4 h-4" />
      {t("sales.clients.pagination.prev")}
    </button>

    <div className="flex items-center gap-1">
      {pageButtons.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => goToPage(p)}
          disabled={loading}
          className={[
            "min-w-[40px] px-3 py-2 rounded-xl border text-sm",
            p === safePageNumber
              ? "bg-purple-600/60 border-purple-300/30 text-white"
              : "bg-white/10 hover:bg-white/15 border-white/20 text-white",
            loading ? "opacity-50" : "",
          ].join(" ")}
          aria-current={p === safePageNumber ? "page" : undefined}
          title={t("sales.clients.pagination.pageOf", { page: p, pages: totalPages })}
        >
          {p}
        </button>
      ))}
    </div>

    <button
      type="button"
      onClick={() => goToPage(safePageNumber + 1)}
      disabled={loading || safePageNumber >= totalPages}
      className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white disabled:opacity-50 flex items-center gap-2"
    >
      {t("sales.clients.pagination.next")}
      <ChevronLeft className="w-4 h-4" />
    </button>
  </div>
</div>

      </div>

      {openModal && (
        <ClientFormModal
          dataBlocks={dataBlocks || []}
          initialData={editing}
          onClose={closeModal}
          onSubmit={async (payload) => {
            const editId = editing ? safeId(editing) : null;

            if (editId != null) {
              const ok = await onUpdate?.(Number(editId), payload);
              if (ok) closeModal();
            } else {
              const ok = await onCreate?.(payload);
              if (ok) closeModal();
            }
          }}
        />
      )}
    </div>
  );
}
